import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Tabs, Spin, Form, Input, Typography, Space, Button, Tooltip, Divider, InputNumber } from 'antd';
import { InfoCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { isEmpty } from 'underscore';
import { AppDispatch, CombinedStates } from '../../core/types';
import { getBvmSurveys, saveBvmSurvey } from '../../core/bvm/bvmApi';
import { BvmSurveyQuestion } from '../../core/bvm/types';
import './style.scss';
import SchoolBvmSurveyStatistics from './SchoolBvmSurveyStatistics';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface SchoolBvmSurveysProps {
    schoolId: number;
}

const SchoolBvmSurveys: React.FC<SchoolBvmSurveysProps> = ({ schoolId }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const [selectedCategory, setSelectedCategory] = useState<string>('');

    const {
        surveys,
        period,
        isFetching,
        error,
    } = useSelector((state: CombinedStates) => state.bvm);

    const selectedSurvey = surveys.find(survey => survey.id.toString() === selectedCategory);

    useEffect(() => {
        if (schoolId) {
            dispatch(getBvmSurveys({ school_id: schoolId }));
        }
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (selectedSurvey) {
            const initialValues: Record<string, string | number> = {};
            selectedSurvey.questions.forEach(question => {
                if (question.type === 'text') {
                    if (question.special === 'balai') {
                        const num = parseFloat(question.answer);
                        initialValues[`question_${question.id}`] = Number.isNaN(num) ? undefined as unknown as number : num;
                    } else {
                        initialValues[`question_${question.id}`] = question.answer || '';
                    }
                }
            });
            form.setFieldsValue(initialValues);
        }
    }, [selectedSurvey, form]);

    // Handle save survey success/error
    useEffect(() => {
        // This will be handled by the Redux state changes
        // We can add success/error handling here if needed
    }, []);

    const handleCategoryChange = (activeKey: string) => {
        setSelectedCategory(activeKey);
    };

    const handleFormSubmit = (values: Record<string, any>) => {
        if (!selectedSurvey) return;

        const answers: Record<string, string> = {};

        // Filter only text question answers
        Object.keys(values).forEach(key => {
            const value = values[key];
            if (key.startsWith('question_') && value) {
                answers[key] = String(value);
            }
        });

        const saveParams = {
            category_id: selectedSurvey.id,
            school_id: schoolId,
            answers,
        };

        dispatch(saveBvmSurvey(saveParams));
    };

    const handleFormFinishFailed = (errorInfo: { errorFields?: Array<{ name: string | number | (string | number)[] }> }) => {
        // Gauname pirmą klaidą iš errorInfo
        const firstError = errorInfo.errorFields?.[0];
        if (firstError) {
            // Scrolliname į pirmą klaidingą lauką
            form.scrollToField(firstError.name);
        }
    };

    const renderQuestion = (question: BvmSurveyQuestion, index: number, allQuestions: BvmSurveyQuestion[]) => {
        if (question.type === 'paragraph') {
            return (
                <div key={question.id} className="paragraph-question">
                    <Text strong>{`${question.code || ''} ${question.title}`}</Text>
                    {question.description && (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">{question.description}</Text>
                        </div>
                    )}
                </div>
            );
        }

        if (question.type === 'text') {
            // Check if this is the first question with special field
            const isFirstSpecialQuestion = !isEmpty(question.special)
                && allQuestions.slice(0, index).every(q => isEmpty(q.special));

            const divider = isFirstSpecialQuestion ? <Divider /> : null;

            // Dedicated numeric input block for special === 'balai'
            if (question.special === 'balai') {
                return (
                    <div key={question.id} className="text-question">
                        {divider}
                        <Form.Item
                            name={`question_${question.id}`}
                            label={`${question.code || ''} ${question.title}`}
                            extra={question.description}
                            rules={[
                                { required: true, message: 'Prašome įvesti balus' },
                                { type: 'number' as const, min: 0, max: 100, message: 'Leidžiamos reikšmės nuo 0 iki 100' },
                            ]}>
                            <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="Įveskite balus (0-100)" />
                        </Form.Item>
                    </div>
                );
            }

            // Default text area for other questions
            return (
                <div key={question.id} className="text-question">
                    {divider}
                    <Form.Item
                        name={`question_${question.id}`}
                        label={`${question.code || ''} ${question.title}`}
                        extra={question.description}
                        rules={[{ required: true, message: 'Prašome užpildyti šį lauką' }]}>
                        <TextArea rows={4} placeholder="Įveskite atsakymą..." />
                    </Form.Item>
                </div>
            );
        }

        return null;
    };

    if (isFetching) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text>Kraunamos BVM apklausos...</Text>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text type="danger">Įvyko klaida kraunant apklausas</Text>
                </div>
            </Card>
        );
    }

    if (surveys.length === 0) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text>Nėra prieinamų BVM apklausų</Text>
                </div>
            </Card>
        );
    }

    return (
        <div className="school-bvm-surveys">
            <Tabs defaultActiveKey="surveys">
                <TabPane tab="Apklausos" key="surveys">
                    <Row gutter={16}>
                        <Col span={5}>
                            <Card title="Apklausų kategorijos" size="small">
                                <Tabs
                                    tabPosition="left"
                                    activeKey={selectedCategory}
                                    onChange={handleCategoryChange}
                                    className="category-tabs"
                                    style={{ width: '100%' }}>
                                    {surveys.map((survey) => (
                                        <TabPane
                                            tab={(
                                                <Tooltip title={survey.title} placement="right">
                                                    <div style={{ textAlign: 'left', maxWidth: '100%' }}>
                                                        <div style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: '250px',
                                                        }}>
                                                            {survey.title}
                                                        </div>
                                                        <Text type={survey.is_filled ? 'success' : 'warning'}>
                                                            {survey.is_filled ? 'Užpildyta' : 'Neužpildyta'}
                                                        </Text>
                                                    </div>
                                                </Tooltip>
                                            )}
                                            key={survey.id.toString()} />
                                    ))}
                                </Tabs>
                            </Card>
                        </Col>
                        <Col span={19}>
                            <Card title={selectedSurvey?.title || 'Pasirinkite kategoriją'}>
                                {selectedSurvey ? (
                                    <div>
                                        <div className="survey-info">
                                            <Title level={4}>{selectedSurvey.title}</Title>
                                            {selectedSurvey.description && (
                                                <Text type="secondary">{selectedSurvey.description}</Text>
                                            )}
                                            <div style={{ marginTop: 8 }}>
                                                <Text strong>Laikotarpis: </Text>
                                                <Text>{period}</Text>
                                            </div>
                                            <div>
                                                <Text strong>Statusas: </Text>
                                                <Text type={selectedSurvey.is_filled ? 'success' : 'warning'}>
                                                    {selectedSurvey.is_filled ? 'Užpildyta' : 'Neužpildyta'}
                                                </Text>
                                            </div>
                                            {selectedSurvey.submitted_at && (
                                                <div>
                                                    <Text strong>Pateikta: </Text>
                                                    <Text>{selectedSurvey.submitted_at}</Text>
                                                </div>
                                            )}
                                        </div>

                                        <div className="question-section">
                                            <Form
                                                form={form}
                                                layout="vertical"
                                                onFinish={handleFormSubmit}
                                                onFinishFailed={handleFormFinishFailed}>
                                                {[...selectedSurvey.questions]
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((question, index, allQuestions) => renderQuestion(question, index, allQuestions))}

                                                <Form.Item className="form-actions">
                                                    <Space>
                                                        <Button type="primary" htmlType="submit">
                                                            Išsaugoti atsakymus
                                                        </Button>
                                                    </Space>
                                                </Form.Item>
                                            </Form>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '50px' }}>
                                        <Text type="secondary">Pasirinkite apklausos kategoriją</Text>
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </TabPane>
                <TabPane tab="Statistika" key="statistics">
                    <SchoolBvmSurveyStatistics schoolId={schoolId} />
                </TabPane>
                <TabPane tab="Ataskaitų pateikimas" key="ataskaitos">
                    <Card className="ataskaitos-info-card">
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Space align="start" size="middle">
                                <InfoCircleOutlined style={{ fontSize: 22, color: '#1890ff', marginTop: 2 }} />
                                <div>
                                    <Text strong style={{ display: 'block', marginBottom: 4 }}>BVM ataskaitos 2025 m.</Text>
                                    <Text type="secondary">
                                        Informuojame, kad BVM ataskaitas už 2025 metus rekomenduojama laikinai talpinti pasinaudojant žemiau pateikta nuoroda.
                                    </Text>
                                </div>
                            </Space>
                            <Button
                                type="primary"
                                href="https://docs.google.com/forms/d/e/1FAIpQLSfY9_HYMOiTQCkqF-gUdA5ZpCZ_sVF4WupzGVms59I7cXG7hg/viewform"
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<LinkOutlined />}
                                size="large">
                                Ataskaitos pateikimas
                            </Button>
                        </Space>
                    </Card>
                </TabPane>
            </Tabs>
        </div>
    );
};

export default SchoolBvmSurveys;
